import {
	EditBrokerDetailsPayload,
	BulkReassignBrokerProjectsPayload,
	BulkReassignBrokerProjectsResult,
	NewBrokerAccountData,
	FinalizeBroker,
	FinalizePartnershipRequest,
	PAGES,
	RegisterPasswordType,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useOriasAndSirenContext } from "../../context/oriasAndSirenContext";
import { AddUserFormData } from "../../pages/brokers/brokersPage/components/tabs/usersTabFiles/useAddUserForm";
import { privateAxios, publicAxios } from "../axios";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";

const createNewBrokerAccount = async (newEmail: NewBrokerAccountData) => {
	await publicAxios.post("/societies/newBrokerAccount", newEmail);
	return newEmail;
};

export const useCreateNewBrokerAccount = () => {
	const navigate = useNavigate();
	const { setNewBrokerAccountData: setEmailForNewBrokerAccount } =
		useOriasAndSirenContext();

	return useMutation({
		mutationFn: createNewBrokerAccount,
		onSuccess: (data) => {
			setEmailForNewBrokerAccount(data);
			navigate(PAGES.SEND_EMAIL);
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

const chooseFirstPassword = async (token: RegisterPasswordType) => {
	await publicAxios.post("/users/choose-first-password", token);
};

export const useChooseFirstPassword = () => {
	const navigate = useNavigate();

	return useMutation({
		mutationFn: chooseFirstPassword,
		onSuccess: () => {
			navigate(PAGES.LOGIN);
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

const finalizeBroker = async (Broker: FinalizeBroker) => {
	await privateAxios.post("/societies/finalize-broker-inscription", Broker);
};

const finalizeBrokerForId = async (
	brokerId: string,
	Broker: FinalizeBroker,
) => {
	await privateAxios.post(
		`/societies/finalize-broker-inscription/${brokerId}`,
		Broker,
	);
};

export const useFinalizeBroker = () => {
	return useMutation({
		mutationFn: finalizeBroker,
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

export const useFinalizeBrokerForId = () => {
	return useMutation({
		mutationFn: ({
			brokerId,
			broker,
		}: { brokerId: string; broker: FinalizeBroker }) =>
			finalizeBrokerForId(brokerId, broker),
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			if (error?.response?.status === 403) {
				toast.error("Vous n'êtes pas autorisé à modifier ce broker");
				return;
			}
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
		onSuccess: () => {
			toast.success(
				"Les informations du broker ont été mises à jour avec succès",
			);
		},
	});
};

const saveAnalysePartnership = async (broker: FinalizePartnershipRequest) => {
	await privateAxios.post("/societies/save-analyse-partnership", broker);
};

export const useSaveAnalysePartneship = () => {
	const navigate = useNavigate();

	return useMutation({
		mutationFn: saveAnalysePartnership,
		onSuccess: (_, variables) => {
			const brokerId = variables.society.id;
			navigate(
				`${PAGES.BROKERS.show.withParams(brokerId.toString())}?tab=taches`,
			);
			toast("Les informations ont été enregistrées");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

const finalSaveAnalysePartnership = async (
	broker: FinalizePartnershipRequest,
) => {
	await privateAxios.post("/societies/final-save-analyse-partnership", broker);
};

export const useFinalSaveAnalysePartneship = () => {
	const navigate = useNavigate();

	return useMutation({
		mutationFn: finalSaveAnalysePartnership,
		onSuccess: (_, variables) => {
			const brokerId = variables.society.id;
			navigate(
				`${PAGES.BROKERS.show.withParams(brokerId.toString())}?tab=taches`,
			);
			toast("Les informations ont été enregistrées et validées");
		},
		onError: () => {
			toast("Une erreur est survenue", {
				description: "Erreur lors de l'enregistrement des données.",
			});
			toast("Une erreur est survenue", {
				description: "Erreur lors de l'enregistrement des données.",
			});
		},
	});
};

const updateBrokerDetails = async (params: {
	id: number;
	data: EditBrokerDetailsPayload;
}) => {
	await privateAxios.patch(
		`/societies/broker/${params.id}/details`,
		params.data,
	);
};

const reassignBrokerProjects = async (params: {
	brokerId: number;
	data: BulkReassignBrokerProjectsPayload;
}): Promise<BulkReassignBrokerProjectsResult> => {
	const { data } = await privateAxios.patch<BulkReassignBrokerProjectsResult>(
		`/societies/broker/${params.brokerId}/projects/reassign`,
		params.data,
	);
	return data;
};

const uploadBrokerDocument = async (params: {
	brokerId: number;
	name: string;
	key: string;
	bucket: string;
	mimeType: string;
	type: string;
}) => {
	const { data } = await privateAxios.post(
		`/societies/broker/${params.brokerId}/documents`,
		{
			name: params.name,
			key: params.key,
			bucket: params.bucket,
			mimeType: params.mimeType,
			type: params.type,
		},
	);
	return data;
};

const updateBrokerCanGenerateApiKeys = async (params: {
	id: number;
	canGenerateApiKeys: boolean;
}) => {
	await privateAxios.patch(
		`/societies/broker/${params.id}/can-generate-api-keys`,
		{ canGenerateApiKeys: params.canGenerateApiKeys },
	);
};

export const useUpdateBrokerCanGenerateApiKeys = (brokerId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (canGenerateApiKeys: boolean) =>
			updateBrokerCanGenerateApiKeys({ id: brokerId, canGenerateApiKeys }),
		onSuccess: async () => {
			toast.success("Autorisation de génération des clés API mise à jour");
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.broker(brokerId),
			});
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.all,
			});
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			if (error?.response?.status === 403) {
				toast.error("Vous n'êtes pas autorisé à modifier ce paramètre");
				return;
			}
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

export const useUpdateBrokerDetails = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateBrokerDetails,
		onSuccess: async (_, variables) => {
			toast("Les modifications ont été enregistrées");
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.broker(variables.id),
			});
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.all,
			});
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.societiesBrokers(),
			});
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			if (error?.response?.status === 403) {
				toast.error("Vous n'êtes pas autorisé à faire ces modifications");
				return;
			}
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

export const useReassignBrokerProjects = (brokerId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: BulkReassignBrokerProjectsPayload) =>
			reassignBrokerProjects({ brokerId, data }),
		onSuccess: async (result) => {
			toast.success("Apporteur des projets mis à jour", {
				description: `${result.updatedConstructionSitesCount} chantier(s) et ${result.updatedPropositionsCount} proposition(s) mis à jour.`,
			});
			await queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.all,
			});
			await queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.all,
			});
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

export const useUploadBrokerDocument = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: uploadBrokerDocument,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDocuments(variables.brokerId),
			});
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Upload document error:", error);

			let errorMessage = "Erreur lors de l'upload du document";

			if (error.response?.status === 403) {
				errorMessage =
					"Accès interdit : vous n'avez pas l'autorisation d'uploader ce document";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 413) {
				errorMessage = "Le fichier est trop volumineux";
			} else if (error.response?.status === 415) {
				errorMessage = "Type de fichier non supporté";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			} else {
				const fallbackMessage = getErrorMessage(error);
				errorMessage = fallbackMessage;
			}

			toast.error(errorMessage);
		},
	});
};

export const useAddUserMutation = (societyId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: AddUserFormData) => {
			const response = await privateAxios.post(
				`/societies/${societyId}/users`,
				data,
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerUsers(societyId),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerAccounting(societyId),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.broker(societyId),
			});
			toast("L'utilisateur a été créé avec succès", {
				description:
					"Un email d'invitation pour définir son mot de passe a été envoyé.",
			});
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			if (error.response?.status === 409) {
				toast.error("Un utilisateur avec cet email existe déjà");
			} else if (error.response?.status === 400) {
				toast.error("Erreur lors de l'ajout de l'utilisateur", {
					description:
						error.response.data.message ||
						"Les données fournies ne sont pas valides.",
				});
			} else {
				const errorMessage = getErrorMessage(error);
				toast.error("Erreur lors de l'ajout de l'utilisateur", {
					description: errorMessage,
				});
			}
		},
	});
};

const setUserAsSignatory = async (params: {
	brokerId: number;
	userId: number;
	isSignatory: boolean;
}) => {
	const { data } = await privateAxios.patch(
		`/societies/broker/${params.brokerId}/users/${params.userId}/signatory`,
		{
			isSignatory: params.isSignatory,
		},
	);
	return data;
};

export const useSetUserAsSignatory = (brokerId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: setUserAsSignatory,
		onSuccess: (_, variables) => {
			const action = variables.isSignatory ? "défini" : "retiré";
			toast(`Le statut de signataire a été ${action} avec succès`);
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerUsers(brokerId),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerAccounting(brokerId),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.broker(brokerId),
			});
			queryClient.refetchQueries({
				queryKey: societiesQueryKeys.broker(brokerId),
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification du statut de signataire", {
				description: errorMessage,
			});
		},
	});
};

const updateBrokerDuneComment = async (
	brokerId: number,
	duneComment: string | null,
): Promise<void> => {
	await privateAxios.patch(`/societies/broker/${brokerId}/dune-comment`, {
		duneComment,
	});
};

export const useUpdateBrokerDuneComment = (brokerId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (duneComment: string | null) =>
			updateBrokerDuneComment(brokerId, duneComment),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.broker(brokerId),
				refetchType: "active",
			});
		},
	});
};

export const deleteBrokerDocument = async (
	brokerId: number,
	documentId: string,
): Promise<void> => {
	await privateAxios.delete(
		`/societies/broker/${brokerId}/documents/${documentId}`,
	);
};

export const useDeleteBrokerDocumentMutation = (
	brokerId: number,
	options?: {
		onMutate?: (documentId: string) => void;
		onSettled?: () => void;
	},
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (documentId: string) =>
			deleteBrokerDocument(brokerId, documentId),
		onMutate: options?.onMutate,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDocuments(brokerId),
			});
			toast.success("Document supprimé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de la suppression du document");
		},
		onSettled: options?.onSettled,
	});
};
